import wretch from 'wretch';
import { AbortError } from '@fe-learner-common';
import { DiscussionSchema } from '../schemas/discussionSchema';
import * as v from 'valibot';
import QueryStringAddon from 'wretch/addons/queryString';
import AbortAddon from 'wretch/addons/abort';
import { localizedErrorHandlers } from '@fe-core/api';

const GetDiscussionPayloadSchema = v.object({
  payload: v.object({
    posts: v.array(DiscussionSchema),
    total: v.number(),
  }),
});

export type GetDiscussionResponse = v.Output<typeof GetDiscussionPayloadSchema>;

export type TGetDiscussionsParams = {
  pageIndex: number;
  pageSize?: number | null;
};

export const getDiscussions = async ({
  baseUrl,
  courseId,
  participationId,
  activityCid,
  params,
  signal,
}: {
  baseUrl: string;
  courseId: number;
  participationId: string;
  activityCid: string;
  params?: TGetDiscussionsParams;
  signal?: AbortSignal;
}): Promise<GetDiscussionResponse> => {
  const controller = new AbortController();

  const url = `${baseUrl}api/v2/course/${courseId}/participation/${participationId}/discussions/${activityCid}`;
  const instance = wretch(url, {
    credentials: 'include',
    mode: 'cors',
  })
    .addon(AbortAddon())
    .signal(controller)
    .addon(QueryStringAddon)
    .query({
      peek: true,
      ...params,
    })
    .get()
    .onAbort(() => {
      throw new AbortError();
    });

  signal?.addEventListener('abort', () => {
    controller.abort();
  });

  return localizedErrorHandlers(instance).json((response) => {
    const validatedResponse = v.safeParse(GetDiscussionPayloadSchema, response);

    if (validatedResponse?.success) {
      return validatedResponse.output;
    } else {
      console.error(
        'fe-learner-common | getDiscussions | ',
        validatedResponse.issues,
      );
      throw new Error('Invalid resources response');
    }
  });
};

const V1DiscussionWithoutRepliesSchema = v.object({
  read: v.boolean(),
  info: v.object({
    owner: v.boolean(),
    parent: v.nullish(v.number()),
    topicId: v.string(),
    replyAllowed: v.boolean(),
    id: v.number(),
    delete: v.boolean(),
  }),
});

const V1DiscussionSchema = v.merge([
  V1DiscussionWithoutRepliesSchema,
  v.object({
    replies: v.array(V1DiscussionWithoutRepliesSchema),
  }),
]);

const PostReplyResponseSchema = v.object({
  posts: v.array(V1DiscussionSchema),
});

export type PostReplyResponse = v.Output<typeof PostReplyResponseSchema>;

export const postReply = async ({
  baseUrl,
  replyText,
  flirtId,
  postId,
  flirtTagId,
}: {
  baseUrl: string;
  replyText: string;
  flirtId: string;
  flirtTagId: string;
  postId?: number;
}): Promise<PostReplyResponse> => {
  const url = `${baseUrl}flirt/makepost/${flirtId}`;

  const instance = wretch(url, {
    credentials: 'include',
  })
    .addon(AbortAddon())
    .addon(QueryStringAddon)
    .query({
      tag: flirtTagId,
      content: replyText,
      ...(postId ? { postId: postId } : {}),
    })
    .post();

  return localizedErrorHandlers(instance).json((response) => {
    const validatedResponse = v.safeParse(PostReplyResponseSchema, response);

    if (validatedResponse?.success) {
      return validatedResponse.output;
    } else {
      console.error(
        'fe-learner-common | postReply | ',
        validatedResponse.issues,
      );
      throw new Error('Invalid resources response');
    }
  });
};

export const deleteDiscussion = async ({
  baseUrl,
  flirtId,
  postId,
}: {
  baseUrl: string;
  flirtId: string;
  postId: number;
}): Promise<boolean> => {
  const url = `${baseUrl}flirt/delete`;

  const instance = wretch(url, {
    credentials: 'include',
  })
    .addon(QueryStringAddon)
    .query({
      topic: flirtId,
      pid: postId,
    })
    .post();

  return localizedErrorHandlers(instance).json((response) => {
    const validatedResponse = v.safeParse(
      v.object({
        deleted: v.boolean(),
      }),
      response,
    );

    if (validatedResponse?.success) {
      return validatedResponse.output.deleted;
    } else {
      console.error(
        'fe-learner-common | deleteDiscussion | ',
        validatedResponse.issues,
      );
      throw new Error('Invalid resources response');
    }
  });
};
